/**
 * The DOM half of the dual channel event system.
 *
 * Every library event is also a bubbling `CustomEvent` on the trigger element,
 * so a consumer who never touches the instance API can still listen with
 * `el.addEventListener('ctxmenu:open', ...)` or, in jQuery, `$(el).on(...)`.
 *
 * The payload is passed as `detail` by reference, not cloned: a `beforeitems`
 * listener splices the very array the caller is about to render.
 */

import type { EventName, EventPayload } from './types'

/** Every DOM event name the library dispatches carries this prefix. */
export const EVENT_PREFIX = 'ctxmenu:'

/**
 * The events a listener may cancel with `preventDefault()`. Everything else
 * ignores cancellation, on both channels.
 */
export const CANCELABLE: ReadonlySet<EventName> = new Set<EventName>([
  'beforeopen',
  'submenu:open',
  'select',
  'beforeclose',
])

/** The DOM event name for a library event, for example `ctxmenu:submenu:open`. */
export function domEventName(name: EventName): string {
  return `${EVENT_PREFIX}${name}`
}

/**
 * Dispatch one library event on `target`.
 *
 * Returns `false` when the event was cancelable and a listener cancelled it,
 * matching `EventTarget.dispatchEvent`.
 */
export function dispatchDom(
  target: HTMLElement,
  name: EventName,
  payload: EventPayload,
): boolean {
  const event = new CustomEvent<EventPayload>(domEventName(name), {
    bubbles: true,
    // Crossing shadow boundaries matters: the trigger may live inside a web
    // component while the listener sits on the host document.
    composed: true,
    cancelable: CANCELABLE.has(name),
    detail: payload,
  })

  return target.dispatchEvent(event)
}
